import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CategoryDTO, ServiceCardDTO } from '@newflix/shared';
import { api } from '../lib/api.ts';
import { qk } from '../lib/queryClient.ts';
import { Icon } from '../lib/icons.tsx';
import { Poster, rgba } from '../lib/posters.tsx';
import { Carousel } from '../components/Carousel.tsx';
import type { CardBadge } from '../components/Card.tsx';
import { relayHref, RELAY_ANCHOR_PROPS } from '../lib/relay.ts';
import { navigate } from '../lib/router.ts';
import { useRecentlyViewed } from '../lib/recentlyViewed.ts';
import { Skeleton } from '../components/ui/Skeleton.tsx';

interface LandingPageProps {
  favs: string[];
  onFav: (id: string) => void;
  onOpen: (id: string) => void;
}

const HERO_CYCLE_MS = 9000;

function LandingRowSkeletons({ rows = 3 }: { rows?: number }) {
  return (
    <div className="nf-landing-skel-rows" aria-hidden>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="nf-row">
          <Skeleton height={22} width={200} style={{ marginBottom: 14 }} />
          <div className="nf-landing-skel-track">
            {Array.from({ length: 6 }).map((_, j) => (
              <Skeleton key={j} radius={12} className="nf-landing-skel-card" height={0} style={{ height: 'auto' }} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

function prettyUrl(raw: string): string {
  try {
    const u = new URL(raw);
    const path = u.pathname === '/' ? '' : u.pathname.replace(/\/$/, '');
    return `${u.hostname}${path}`;
  } catch {
    return raw.replace(/^https?:\/\//, '').replace(/\/$/, '');
  }
}

export function LandingPage({ favs, onFav, onOpen }: LandingPageProps) {
  const listQ = useQuery({
    queryKey: qk.services.list({ pageSize: 200, sort: 'newest' }),
    queryFn: () => api.listServices({ pageSize: 200, sort: 'newest' }),
  });
  const catsQ = useQuery({
    queryKey: qk.categories.list(),
    queryFn: () => api.listCategories(),
  });
  const featuredQ = useQuery({
    queryKey: qk.featured('hero'),
    queryFn: () => api.featured('hero'),
  });
  const landingQ = useQuery({
    queryKey: qk.landing(),
    queryFn: () => api.landing(),
  });

  const services: ServiceCardDTO[] = listQ.data?.items ?? [];
  const categoryNameBySlug = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of catsQ.data?.items ?? []) m.set(c.slug, c.name);
    return m;
  }, [catsQ.data]);
  const catName = (slug: string | null) => (slug && categoryNameBySlug.get(slug)) ?? slug ?? '미분류';

  const heroes = useMemo<ServiceCardDTO[]>(() => {
    const curated = featuredQ.data?.items ?? [];
    if (curated.length > 0) return curated.slice(0, 4);
    return [...services].sort((a, b) => b.upvotes - a.upvotes).slice(0, 4);
  }, [featuredQ.data, services]);

  const [heroIdx, setHeroIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (heroes.length === 0) return;
    setProgress(0);
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / HERO_CYCLE_MS);
      setProgress(p);
      if (p < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setHeroIdx((i) => (i + 1) % heroes.length);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [heroIdx, heroes.length]);

  const hero = heroes[heroIdx];

  // LCP: keep a high-priority preload for the current hero OG (SPA + post-hydrate).
  useEffect(() => {
    const href = hero?.thumbnail;
    if (!href || typeof document === 'undefined') return;
    const sel = 'link[data-nf-lcp-preload]';
    let link = document.head.querySelector<HTMLLinkElement>(sel);
    if (!link) {
      link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.setAttribute('fetchpriority', 'high');
      link.setAttribute('data-nf-lcp-preload', '1');
      document.head.appendChild(link);
    }
    if (link.href !== href) link.href = href;
  }, [hero?.thumbnail]);

  const [liveUpvotes, setLiveUpvotes] = useState(hero?.upvotes ?? 0);
  useEffect(() => {
    if (!hero) return;
    setLiveUpvotes(hero.upvotes);
    const t = setInterval(() => setLiveUpvotes((v) => v + Math.floor(Math.random() * 2)), 3000);
    return () => clearInterval(t);
  }, [hero?.slug, hero?.upvotes]);

  const landingRows = landingQ.data?.rows ?? [];

  // 최근 본 서비스 (localStorage) → 카드 DTO batch 조회.
  const recentSlugs = useRecentlyViewed();
  const recentQ = useQuery({
    queryKey: ['recently-viewed', recentSlugs.join(',')],
    queryFn: () => api.batchServices(recentSlugs),
    enabled: recentSlugs.length > 0,
  });
  const recentItems = recentQ.data?.items ?? [];

  // 랜덤 발견 — 시드 1개 무작위 선택 → 그 서비스의 /related(임베딩 유사) 추천.
  // 시드는 데이터 첫 도착시 1회만 선택(useState). listQ 재요청(staleTime 만료/포커스)으로
  // services 배열 정체성이 바뀌어도 재셔플 안 함 → 세션 내 안정.
  const [seed, setSeed] = useState<ServiceCardDTO | null>(null);
  useEffect(() => {
    if (!seed && services.length > 0) setSeed(shuffle(services)[0]!);
  }, [services, seed]);
  const relatedQ = useQuery({
    queryKey: qk.services.related(seed?.slug ?? ''),
    queryFn: () => api.relatedServices(seed!.slug),
    enabled: !!seed,
  });
  const discover = relatedQ.data?.items ?? [];

  // 랜덤 카테고리 큐레이션 2개 — 세션 1회 선택 (시드 안정). 데이터 도착 후 shuffle 표시.
  const [randomCats, setRandomCats] = useState<CategoryDTO[]>([]);
  useEffect(() => {
    if (randomCats.length > 0) return;
    const items = catsQ.data?.items ?? [];
    if (items.length === 0) return;
    setRandomCats(shuffle(items).slice(0, 2));
  }, [catsQ.data, randomCats.length]);
  const cat0 = randomCats[0];
  const cat1 = randomCats[1];
  const cat0Q = useQuery({
    queryKey: ['random-cat', cat0?.slug ?? ''],
    queryFn: () => api.getCategory(cat0!.slug, 1, 20),
    enabled: !!cat0,
  });
  const cat1Q = useQuery({
    queryKey: ['random-cat', cat1?.slug ?? ''],
    queryFn: () => api.getCategory(cat1!.slug, 1, 20),
    enabled: !!cat1,
  });
  // 화면 표시 전에 random sort. data 정체성 바뀔 때만 재셔플.
  const cat0Items = useMemo(() => (cat0Q.data ? shuffle(cat0Q.data.items) : []), [cat0Q.data]);
  const cat1Items = useMemo(() => (cat1Q.data ? shuffle(cat1Q.data.items) : []), [cat1Q.data]);

  const weekBadge = (_s: ServiceCardDTO, i: number): CardBadge | null =>
    i < 3 ? { label: `#${i + 1}` } : null;
  const freshBadge = (s: ServiceCardDTO): CardBadge | null =>
    s.daysSincePublished <= 3
      ? { label: 'LIVE', live: true }
      : s.daysSincePublished <= 7
        ? { label: 'NEW' }
        : null;

  // ── 랜딩 캐러셀(행) 목록 — 단일 배열로 통합해 인덱스 부여 (행 단위 current 회전용) ──
  const carousels = useMemo(() => {
    const list: {
      key: string;
      title: string;
      emoji?: string;
      services: ServiceCardDTO[];
      badge?: (s: ServiceCardDTO, i: number) => CardBadge | null;
      onTitleClick?: () => void;
    }[] = [];
    if (recentItems.length > 0)
      list.push({ key: 'recent', title: '이어보기', emoji: '🔁', services: recentItems });
    for (const row of landingRows) {
      if (row.services.length === 0) continue;
      list.push({
        key: row.kind === 'system' ? `sys:${row.systemKey}` : `col:${row.code ?? row.rowId}`,
        title: row.title,
        emoji: row.emoji ?? undefined,
        services: row.services,
        badge:
          row.systemKey === 'popular' ? weekBadge : row.systemKey === 'newest' ? freshBadge : undefined,
        onTitleClick: row.code ? () => navigate('/col/' + row.code) : undefined,
      });
    }
    if (seed && discover.length > 0)
      list.push({
        key: 'discover',
        title: `'${seed.name}' 좋아하면 이것도`,
        emoji: '🎲',
        services: discover,
        onTitleClick: () => onOpen(seed.slug),
      });
    if (cat0 && cat0Items.length > 0)
      list.push({
        key: 'cat0',
        title: cat0.name,
        emoji: cat0.icon ?? '🎲',
        services: cat0Items,
        onTitleClick: () => navigate('/c/' + cat0.slug),
      });
    if (cat1 && cat1Items.length > 0)
      list.push({
        key: 'cat1',
        title: cat1.name,
        emoji: cat1.icon ?? '🎲',
        services: cat1Items,
        onTitleClick: () => navigate('/c/' + cat1.slug),
      });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentItems, landingRows, discover, seed, cat0, cat1, cat0Items, cat1Items]);

  // current 캐러셀(행) = 스크롤/노출 위치로 결정. 화면에서 가장 많이 보이는 행이 active.
  const mainRef = useRef<HTMLElement | null>(null);
  const [activeRow, setActiveRow] = useState(0);
  // 행 구성 변화를 식별하는 안정 키 — 길이만으로는 동일길이 교체를 못 잡음.
  const carouselsKey = carousels.map((c) => c.key).join('|');
  // 행 수 변경 시 activeRow 가 범위를 벗어나지 않도록 보정.
  useEffect(() => {
    setActiveRow((r) => (carousels.length > 0 ? Math.min(r, carousels.length - 1) : 0));
  }, [carousels.length]);
  useEffect(() => {
    const root = mainRef.current;
    if (!root || carousels.length === 0) return;
    const rows = Array.from(root.querySelectorAll<HTMLElement>('.nf-row[data-row-index]'));
    if (rows.length === 0) return;
    const ratios = new Map<number, number>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const idx = Number((e.target as HTMLElement).dataset.rowIndex);
          ratios.set(idx, e.intersectionRatio);
        }
        let best = -1;
        let bestRatio = 0;
        for (const [idx, r] of ratios) {
          if (r > bestRatio) {
            bestRatio = r;
            best = idx;
          }
        }
        if (best >= 0) setActiveRow(best);
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1], rootMargin: '-10% 0px -10% 0px' }
    );
    rows.forEach((el) => io.observe(el));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselsKey]);

  if (listQ.isPending && services.length === 0) {
    return (
      <main className="nf-page">
        <section className="nf-cine">
          <div className="nf-cine-atmos skel-shimmer" />
        </section>
        <LandingRowSkeletons rows={3} />
      </main>
    );
  }
  if (listQ.error || !hero) {
    return (
      <main className="nf-page" style={{ padding: '160px 56px', textAlign: 'center', color: 'var(--fg-muted)' }}>
        {listQ.error ? `로딩 실패: ${listQ.error.message}` : '아직 등록된 서비스가 없어요.'}
      </main>
    );
  }

  return (
    <main className="nf-page" ref={mainRef}>
      <section className="nf-cine">
        <div
          className="nf-cine-atmos"
          style={{
            background: `
              radial-gradient(70% 55% at 80% 30%, ${rgba(hero.brandColor, 0.55)} 0%, transparent 60%),
              radial-gradient(60% 50% at 25% 75%, ${rgba(hero.brandColor, 0.22)} 0%, transparent 60%),
              radial-gradient(40% 40% at 50% 50%, ${rgba(hero.brandColor, 0.08)} 0%, transparent 70%),
              linear-gradient(180deg, #121212 0%, ${hero.accentColor} 60%, #121212 100%)`,
          }}
        />
        <div className="nf-cine-noise" />
        <div className="nf-cine-vignette" />

        <div className="nf-cine-stage">
          <div className="nf-cine-main">
            <div className="nf-cine-main-bezel">
              <div className="nf-cine-main-chrome">
                <span className="nf-cine-dot" style={{ background: '#ff5f57' }} />
                <span className="nf-cine-dot" style={{ background: '#febc2e' }} />
                <span className="nf-cine-dot" style={{ background: '#28c840' }} />
                {hero.url ? (
                  <a
                    className="nf-cine-url"
                    href={relayHref(hero.slug, { source: 'newflix', medium: 'hero_chrome' })}
                    {...RELAY_ANCHOR_PROPS}
                    title={hero.url}
                    data-outbound={hero.url}
                  >
                    {prettyUrl(hero.url)}
                  </a>
                ) : (
                  <div className="nf-cine-url">{hero.maker.handle.replace('@', '')}</div>
                )}
              </div>
              <div
                className="nf-cine-main-screen"
                onClick={() => onOpen(hero.slug)}
                style={{ cursor: 'pointer' }}
                role="link"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onOpen(hero.slug);
                }}
                title={`${hero.name} 자세히 보기`}
              >
                <Poster service={hero} variant="hero" imageUrl={hero.thumbnail} />
              </div>
            </div>
          </div>
        </div>

        <div className="nf-cine-fade" />

        <div className="nf-cine-content">
          <div className="nf-cine-eyebrow">
            <span
              className="nf-cine-eyebrow-bar"
              style={{ background: hero.brandColor, color: hero.brandColor }}
            />
            <span className="nf-cine-eyebrow-week">이번 주 #{heroIdx + 1}</span>
            <span className="nf-cine-eyebrow-dot">·</span>
            <span>{catName(hero.category)}</span>
          </div>
          <h1 className="nf-cine-title">{hero.name}</h1>
          <p className="nf-cine-tag">{hero.tagline}</p>
          <div className="nf-cine-cta">
          <div className="nf-cine-meta">
            <div className="nf-cine-meta-maker">
              <div
                className="nf-cine-meta-av"
                style={{
                  background: hero.faviconUrl
                    ? '#fff'
                    : `linear-gradient(135deg, ${hero.brandColor}, ${hero.accentColor})`,
                  overflow: 'hidden',
                }}
              >
                {hero.faviconUrl ? (
                  <img
                    src={hero.faviconUrl}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  hero.maker.name[0]
                )}
              </div>
              <div>
                <div className="nf-cine-meta-mn">{hero.maker.name}</div>
                {hero.maker.region && <div className="nf-cine-meta-mh">{hero.maker.region}</div>}
              </div>
            </div>
            {liveUpvotes > 100 && (
               <div className="nf-cine-meta-stat">
               <div className="nf-cine-meta-stat-l">업보트</div>
               <div className="nf-cine-meta-stat-v">
                 <Icon.upArrow style={{ color: 'var(--positive)' }} /> {liveUpvotes.toLocaleString()}
               </div>
             </div>
            )}
           
            {/*
            <div className="nf-cine-meta-stat">
              <div className="nf-cine-meta-stat-l">등록</div>
              <div className="nf-cine-meta-stat-v">{hero.daysSincePublished}일 전</div>
            </div>
            */}
          </div>
          <div className="nf-cine-actions">
            <button className="nf-btn primary" onClick={() => onOpen(hero.slug)}>
              <Icon.play /> 자세히 보기
            </button>
            <button className="nf-btn ghost" onClick={() => onFav(hero.slug)}>
              {favs.includes(hero.slug) ? (
                <>
                  <Icon.check /> 찜함
                </>
              ) : (
                <>
                  <Icon.plus /> 찜하기
                </>
              )}
            </button>
          </div>
          </div>

          <div className="nf-cine-pager">
            {heroes.map((s, i) => (
              <div
                key={s.slug}
                className={`nf-cine-pager-item ${i === heroIdx ? 'active' : ''}`}
                onClick={() => setHeroIdx(i)}
              >
                <div className="nf-cine-pager-rank">0{i + 1}</div>
                <div className="nf-cine-pager-bar">
                  <div
                    className="nf-cine-pager-fill"
                    style={{
                      width: i === heroIdx ? `${progress * 100}%` : i < heroIdx ? '100%' : '0%',
                      background: i === heroIdx ? '#fff' : 'rgba(255,255,255,.4)',
                    }}
                  />
                </div>
                <div className="nf-cine-pager-name">{s.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {landingQ.isPending && carousels.length === 0 ? (
        <LandingRowSkeletons rows={3} />
      ) : (
        carousels.map((c, idx) => (
          <Carousel
            key={c.key}
            rowIndex={idx}
            title={c.title}
            emoji={c.emoji}
            services={c.services}
            favs={favs}
            onFav={onFav}
            onOpen={onOpen}
            badge={c.badge}
            onTitleClick={c.onTitleClick}
            active={idx === activeRow}
          />
        ))
      )}
    </main>
  );
}
